import React, { useState, useEffect, useMemo } from 'react';
import { Input, Form, Select } from 'components/shared/FormComponent';
import { message, Upload, Spin } from 'components/shared/AntComponent';
import { Button } from 'components/shared/ButtonComponent';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTicket, saveTicket } from 'services/actions/ticketActions';
import { resetSaveTicketState } from 'services/slices/ticketSlice';
import dayjs from 'dayjs';

const { TextArea } = Input;

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    open: { label: 'Open', className: 'status-open' },
    inprogress: { label: 'In Progress', className: 'status-inprogress' },
    onhold: { label: 'On Hold', className: 'status-onhold' },
    resolved: { label: 'Resolved', className: 'status-resolved' },
    closed: { label: 'Closed', className: 'status-closed' },
};

const getStatusConfig = (status: string) => {
    const key = (status || '').toLowerCase().replace(/\s+/g, '');
    return STATUS_CONFIG[key] || { label: status || '-', className: 'status-default' };
};

const cleanFileNameAndExt = (urlStr: string) => {
    if (!urlStr) return { fileName: 'Attachment', ext: 'file' };
    
    try {
        const parsedUrl = new URL(urlStr, window.location.origin);
        const pathname = decodeURIComponent(parsedUrl.pathname);
        const rawFileName = pathname.split('/').filter(Boolean).pop() || 'Attachment';
        const extMatch = rawFileName.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1].toLowerCase() : 'file';

        return { fileName: rawFileName, ext };
    } catch {
        const cleanUrl = urlStr.split('?')[0];
        const rawFileName = decodeURIComponent(cleanUrl.split('/').filter(Boolean).pop() || 'Attachment');
        const extMatch = rawFileName.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1].toLowerCase() : 'file';
        return { fileName: rawFileName, ext };
    }
};

const AddTickets: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { ticketInfo, saveTicketLoading, saveTicketSuccess, saveTicketError } = useSelector((state: any) => state.ticket);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]);
    const isEditMode = !!location.state?.rowData?.id;
    const userRole = sessionStorage.getItem('role');
    const rowData = location?.state?.rowData;
    const requesterName = `${sessionStorage.getItem('firstname') || ''} ${sessionStorage.getItem('lastname') || ''}`.trim();
    const [attachmentLoading, setAttachmentLoading] = useState(false);
    const headerStatus = rowData?.status ? getStatusConfig(rowData.status) : null;
    const accountId = Number(sessionStorage.getItem('accountid'));
    const [statusAction, setStatusAction] = useState<"closed" | "open" | null>(null);

    useEffect(() => {
        dispatch(resetSaveTicketState());
    }, [dispatch]);

    const handleBack = () => {
        setFileList([]);
        navigate('/tickets');
    };

    const handleViewTracking = () => {
        navigate('/tickets/ticket-details', { state: { rowData } });
    };

    const handleUploadChange = (info: any) => {
        const nextFileList = (info?.fileList || []).filter((file: any) => file.status !== 'error');
        setFileList(nextFileList);
        form.setFieldsValue({ upload: nextFileList.length > 0 ? nextFileList : undefined });
    };

    const handleBeforeUpload = (file: any): boolean => {
        const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/pdf',
            'application/msword', 
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        const isValidFileType =
            allowedTypes.includes(file.type) ||
            /\.(jpe?g|png|pdf|doc|docx)$/i.test(file.name);

        if (!isValidFileType) {
            message.error('You can only upload JPG, PNG, PDF, or Word files!');
            return Upload.LIST_IGNORE as unknown as boolean;
        }

        const isLt10MB = file.size / 1024 / 1024 < 10;
        if (!isLt10MB) {
            message.error('File size must be smaller than 10MB!');
            return Upload.LIST_IGNORE as unknown as boolean;
        }

        return false;
    };

    const categoryOptions = [
        { label: 'Issue', value: 'bug' },
        { label: 'Clarification', value: 'feature' },
    ];

    const priorityOptions = [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
        { label: 'Critical', value: 'critical' },
    ];

    const statusOptions = [
        { label: 'Open', value: 'open' },
        { label: 'In Progress', value: 'inprogress' },
        { label: 'On Hold', value: 'onhold' },
        { label: 'Resolved', value: 'resolved' },
        { label: 'Closed', value: 'closed' },
    ];

    const assignedToOptions = [
        { label: 'Axon EEG Admin', value: 'axon eeg admin' },
        { label: 'Axon EEG Support', value: 'axon eeg support' },
        { label: 'Data Hub Technical Support', value: 'data hub technical support' },
    ];

    const filteredStatusOptions = useMemo(() => {
        if (userRole === 'staff') {
            return [
                { label: 'Open', value: 'open' },
                { label: 'Closed', value: 'closed' },
            ];
        }
        return statusOptions;
    }, [userRole]);

    useEffect(() => {
        if (location.state?.rowData) {
            form.setFieldsValue({
                name: location.state.rowData.name,
                category: location.state.rowData.category,
                description: location.state.rowData.description,
                priority: location.state.rowData.priority,
                status: location.state.rowData.status,
                assigned_to: location.state.rowData.assigned_to,
            });
        }
    }, [location.state, form]);

    useEffect(() => {
        if (location.state?.rowData?.id) {
            setFileList([]);     
            dispatch(getTicket(location.state.rowData.id) as any);
        }
    }, [location.state?.rowData?.id, dispatch]);

    useEffect(() => {
        if (!isEditMode) {
            setFileList([]);
            setAttachmentLoading(false);
            return;
        }

        const filepath = ticketInfo?.ticket?.filepath;

        const filePaths: string[] = !filepath
            ? []
            : Array.isArray(filepath)
            ? filepath.filter(Boolean)
            : [filepath];

        if (filePaths.length === 0) {
            setFileList([]);
            setAttachmentLoading(false);
            return;
        }

        const existingFiles = filePaths.map((filePath, index) => {
            const { fileName } = cleanFileNameAndExt(filePath);
            return {
                uid: `existing-${index}-${fileName}`,
                name: fileName,
                status: 'done',
                url: filePath,
                isExisting: true 
            };
        });

        setFileList(existingFiles);
        setAttachmentLoading(false);
    }, [ticketInfo?.ticket?.filepath, isEditMode]);

    useEffect(() => {
        if (fileList && fileList.length > 0) {
            form.setFieldsValue({ upload: fileList });
        } else {
            form.setFieldsValue({ upload: undefined });
        }
    }, [fileList, form]);

    const onFinish = (values: any) => {
        const formData = new FormData();

        const existingFilePaths = fileList
            .filter((item) => item.isExisting && item.url)
            .map((item) => item.url);

        const inputJson: any = {
            id: rowData?.id || 0,
            name: values.name,
            category: values.category,
            description: values.description,
            status: userRole === 'admin' ? values.status : 'open',
            is_active: true,
            priority: values.priority,
            assigned_to: values.assigned_to || null,
            requester_name: rowData?.requester_name || requesterName,
            ticket_from: rowData?.ticket_from ?? (userRole === 'admin' ? 1 : 2),
            accountid: rowData?.id
                ? rowData?.accountid
                : (userRole === 'admin' ? null : accountId),
            filepath: existingFilePaths 
        };

        const newAttachments = fileList.filter(
            (fileItem) => !fileItem.isExisting && fileItem?.originFileObj
        );

        if (newAttachments.length > 0) {
            newAttachments.forEach((fileItem) => {
                const uploadName = fileItem.name || fileItem.originFileObj?.name;
                formData.append(
                    'File',
                    fileItem.originFileObj,
                    uploadName
                );
            });
        } else if (existingFilePaths.length === 0) {
            formData.append(
                'File',
                new File([], 'empty.txt', {
                    type: 'text/plain',
                }),
                'empty.txt'
            );
        }

        formData.append('InputJson', JSON.stringify(inputJson));

        dispatch(saveTicket(formData) as any);
    };

    useEffect(() => {
        if (saveTicketSuccess) {
            message.success(
    statusAction === "closed"
        ? "Ticket closed successfully"
        : statusAction === "open"
        ? "Ticket reopened successfully"
        : isEditMode
        ? "Ticket updated successfully"
        : "Ticket created successfully"
);

            setStatusAction(null);
            dispatch(resetSaveTicketState());
            navigate('/tickets');
        }
    }, [saveTicketSuccess, statusAction, dispatch, navigate, isEditMode]);

    useEffect(() => {
        if (saveTicketError) {
            const errorMsg = typeof saveTicketError === 'string' 
                ? saveTicketError 
                : (saveTicketError?.message || 'Failed to save the ticket. Please try again.');
            message.error(errorMsg);
        }
    }, [saveTicketError]);

    useEffect(() => {
        if (!isEditMode) {
            form.resetFields();
            setFileList([]);
        }
    }, [isEditMode]);

    return (
        <div className="ticket-form-wrapper p-3">
            <div className="form-header-card">
                <div className="d-flex grid-title-card flex-wrap align-items-start">
                    <div>
                        <h5 className="form-page-title my-auto">{isEditMode ? 'Update ' : 'Add '}Ticket</h5>
                        {isEditMode && rowData ? (
                            <div className="form-meta-row">
                                {rowData.id !== undefined ? <span className="ticket-id-chip">#{rowData.id}</span> : null}
                                {headerStatus ? (
                                    <span className={`status-badge ${headerStatus.className}`}>
                                        <span className="status-dot" />
                                        {headerStatus.label}
                                    </span>
                                ) : null}
                                {rowData.created_on ? (
                                    <span className="form-meta-text">Submitted {dayjs(rowData.created_on).format('MM/DD/YYYY')}</span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="ms-auto d-flex title-btn">
                        {isEditMode ? (
                            <Button className="add-btn outline-btn me-2" onClick={handleViewTracking}>
                                View Tracking
                            </Button>
                        ) : null}
                        <Button type="primary" onClick={handleBack} className="add-btn">
                            Back
                        </Button>
                    </div>
                </div>
            </div>

            <div className="form-body-card my-2 p-2 bg-white template-manager">
                <Form 
                    layout="vertical" 
                    form={form} 
                    onFinish={onFinish} 
                    className="w-100"
                    autoComplete="off"
                >
                    <div className="m-2 border-top-0 mt-4">
                        <div className="card-body row">
                            
                            <div className="row mx-auto">
                                <div className="col-md-12 ps-0">
                                    <Form.Item
                                        name="name"
                                        label="Subject"
                                        rules={[{ required: true, message: 'This field is required' }]}
                                    >
                                        <Input placeholder="Enter ticket subject" />
                                    </Form.Item>
                                </div>
                            </div>

                            <div className="row mx-auto">
                                <div className={userRole === "admin" ? "col-md-3 ps-0" : "col-md-4 ps-0"}>
                                    <Form.Item
                                        name="category"
                                        label="Category"
                                        rules={[{ required: true, message: 'This field is required' }]}
                                    >
                                        <Select className="w-100" options={categoryOptions} placeholder="Select Category" />
                                    </Form.Item>
                                </div>

                                <div className={userRole === "admin" ? "col-md-3 ps-0" : "col-md-4 ps-0"}>
                                    <Form.Item
                                        name="priority"
                                        label="Priority"
                                        rules={[{ required: true, message: 'This field is required' }]}
                                    >
                                        <Select className="w-100" options={priorityOptions} placeholder="Select Priority" />
                                    </Form.Item>
                                </div>

                                <div className={userRole === "admin" ? "col-md-3 ps-0" : "col-md-4 ps-0"}>
                                    <Form.Item
                                        name="assigned_to"
                                        label="Assigned To"
                                        rules={[{ required: true, message: 'This field is required' }]}
                                    >
                                        <Select className="w-100" options={assignedToOptions} placeholder="Select Assignee" />
                                    </Form.Item>
                                </div>

                                {userRole === "admin" && (
                                    <div className="col-md-3 ps-0">
                                        <Form.Item
                                            name="status"
                                            label="Status"
                                            rules={[{ required: true, message: 'This field is required' }]}
                                        >
                                            <Select className="w-100" options={filteredStatusOptions} placeholder="Select Status" />
                                        </Form.Item>
                                    </div>
                                )}
                            </div>

                            <div className="row mx-auto">
                                <div className="col-md-12 ps-0">
                                    <Form.Item 
                                        name="description" 
                                        label="Description" 
                                        rules={[{ required: true, message: 'This field is required' }]}
                                    >
                                        <Input.TextArea autoSize={{ minRows: 4 }} placeholder="Describe the issue..." />
                                    </Form.Item>
                                </div>
                            </div>

                            <div className="row mx-auto">
                                <div className="col-md-12 ps-0 mt-2">
                                    <Form.Item
                                        name="upload"
                                        label="Attachment"
                                        valuePropName="fileList"
                                        getValueFromEvent={(e) =>
                                            Array.isArray(e) ? e : e?.fileList
                                        }
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please upload at least one attachment',
                                            },
                                        ]}
                                    >
                                        <Spin spinning={attachmentLoading}>
                                            <Upload.Dragger
                                                name="file"
                                                multiple={true}
                                                beforeUpload={handleBeforeUpload}
                                                fileList={fileList}
                                                onChange={handleUploadChange}
                                                listType="picture"
                                                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                                itemRender={(originNode, file) => {
                                                    const { fileName, ext } = cleanFileNameAndExt(file.name || file.url || '');
                                                    const isDocument = ['pdf', 'doc', 'docx'].includes(ext);

                                                    if (isDocument) {
                                                        const fileUrl = file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : '');
                                                        
                                                        return (
                                                            <div className="p-3 my-2 border rounded bg-light d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center me-3" style={{ overflow: 'hidden' }}>
                                                                    <span className="badge bg-primary me-2 text-uppercase">
                                                                        {ext.toUpperCase()}
                                                                    </span>
                                                                    <span className="text-truncate fw-500" style={{ maxWidth: '250px' }} title={fileName}>
                                                                        {fileName}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {fileUrl && (
                                                                        <a
                                                                            href={fileUrl}
                                                                            download={fileName}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            Download Attachment
                                                                        </a>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="btn-close ms-2"
                                                                        aria-label="Close"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const updatedList = fileList.filter((item) => item.uid !== file.uid);
                                                                            setFileList(updatedList);
                                                                            form.setFieldsValue({ upload: updatedList.length > 0 ? updatedList : undefined });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    return originNode;
                                                }}
                                            >
                                                <p className="ant-upload-drag-icon"></p>
                                                <h6 className="mb-2">Drag & Drop your file here</h6>
                                                <p className="mb-1">or click to browse</p>
                                                <small>JPG, PNG, PDF, DOC, DOCX (Max 10MB)</small>
                                            </Upload.Dragger>
                                        </Spin>
                                    </Form.Item>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="text-end rounded mt-2 d-flex submit-btn-grp">
                        <Button className="add-btn outline-btn ms-auto me-2" onClick={handleBack}>Cancel</Button>
                        <Button
                            className="add-btn"
                            htmlType="submit"
                            type="primary"
                            loading={saveTicketLoading || attachmentLoading}
                        >
                            {isEditMode ? 'Update Ticket' : 'Create Ticket'}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default AddTickets;